import { useMemo } from 'react'

import type { IReportEvent } from '@/hooks/useReportStream'

const MAX_FINDINGS = 50

export enum EFindingKind {
  NEWS = 'news',
  FLAGGED_POST = 'flagged_post',
  FOOTPRINT = 'footprint',
  FACE_MATCH = 'face_match',
  PATTERN = 'pattern',
  RELATIONSHIP = 'relationship',
}

export enum EPatternType {
  BEHAVIORAL = 'behavioral',
  THEMATIC = 'thematic',
  PLATFORM_SPECIFIC = 'platform-specific',
}

export enum ERelationshipTone {
  FRIENDLY = 'friendly',
  PROFESSIONAL = 'professional',
  COLLABORATIVE = 'collaborative',
  ANTAGONISTIC = 'antagonistic',
  AMBIVALENT = 'ambivalent',
}

/**
 * Accepts only URLs that look usable. Rejects empty strings and the malformed
 * `undefined/unsafe/...` shape emitted when imgproxy env vars are missing — the
 * ProfileAvatar `onError` would fall back to the platform icon anyway, but we
 * don't want to promote a broken URL to the subject header in the first place.
 */
function isUsableImageUrl(url: string | undefined | null): url is string {
  if (!url) return false
  if (url.startsWith('undefined')) return false
  return true
}

export interface ISubjectIdentity {
  firstName: string
  lastName: string
  imageUrl?: string
  /** Whether the identity has been verified (e.g. via face match). */
  verified?: boolean
}

export interface ISocialMediaFound {
  platform: string
  userName: string
  url?: string
  profileImgUrl?: string
  bio?: string
  followers: number
  following: number
  postsCount: number
  timestamp: string
}

export interface IFindingItem {
  id: string
  kind: EFindingKind
  platform?: string
  userName?: string
  type?: string
  source?: string
  content?: string
  flags?: string[]
  url?: string
  imageUrl?: string
  date?: string
  timestamp: string
  /** Pattern-specific (kind === EFindingKind.PATTERN) */
  patternType?: EPatternType
  confidence?: number
  /** Relationship-specific (kind === EFindingKind.RELATIONSHIP) */
  person?: string
  handles?: string[]
  relationshipTone?: ERelationshipTone
}

export interface IContactEmail {
  email: string
  type?: string
}

export interface IContactPhone {
  phone: string
}

export interface IContactAddress {
  fullAddress?: string
  locality?: string
  region?: string
  country?: string
}

export interface IContactInfo {
  emails: IContactEmail[]
  phones: IContactPhone[]
  addresses: IContactAddress[]
}

export interface IReportData {
  subject: ISubjectIdentity | null
  socialMedia: ISocialMediaFound[]
  findings: IFindingItem[]
  contactInfo: IContactInfo
  totals: {
    posts: number
    following: number
    followers: number
  }
  stats: {
    redFlagsDetected: number
  }
  hasScraperData: boolean
  /** Behavioral overview from analyst (first paragraph of digital footprint). */
  behavioralSummary: string | null
}

export function useReportData(
  events: IReportEvent[],
  initialSubject?: ISubjectIdentity | null,
): IReportData {
  return useMemo(() => {
    // Form-input values are user-confirmed; backend events may only fill gaps,
    // never overwrite name or photo we already have.
    let subject: ISubjectIdentity | null = initialSubject ?? null
    const socialMedia: ISocialMediaFound[] = []
    const findings: IFindingItem[] = []

    let totalPosts = 0
    let totalFollowing = 0
    let totalFollowers = 0
    let redFlagsDetected = 0
    let hasScraperData = false
    const contactInfo: IContactInfo = { emails: [], phones: [], addresses: [] }

    let behavioralSummary: string | null = null
    const seenFindings = new Set<string>()

    for (let i = 0; i < events.length; i++) {
      const event = events[i]
      const { type, data, timestamp } = event

      switch (type) {
        case 'find:subject_identified': {
          subject = {
            firstName: subject?.firstName || (data?.firstName as string) || '',
            lastName: subject?.lastName || (data?.lastName as string) || '',
            imageUrl:
              subject?.imageUrl || (data?.imageUrl as string | undefined),
            verified:
              (data?.verified as boolean | undefined) ?? subject?.verified,
          }
          break
        }

        case 'analysis:summary_complete': {
          if (data?.summary) behavioralSummary = data.summary as string
          break
        }

        case 'find:profile_found': {
          const platform = (data?.platform as string) || ''
          const userName = (data?.userName as string) || ''
          if (socialMedia.some((s) => s.platform === platform)) break
          socialMedia.push({
            platform,
            userName,
            url: data?.url as string | undefined,
            followers: 0,
            following: 0,
            postsCount: 0,
            timestamp,
          })
          break
        }

        case 'scraper:profile_scraped': {
          const platform = (data?.platform as string) || ''
          const userName = (data?.userName as string) || ''
          const rawProfileImgUrl = data?.profileImgUrl as string | undefined
          const profileImgUrl = isUsableImageUrl(rawProfileImgUrl)
            ? rawProfileImgUrl
            : undefined

          // Enrich an existing surfaced profile or add a new one
          const existingIdx = socialMedia.findIndex(
            (s) => s.platform === platform,
          )
          if (existingIdx !== -1) {
            socialMedia[existingIdx] = {
              ...socialMedia[existingIdx],
              userName: userName || socialMedia[existingIdx].userName,
              url:
                (data?.url as string | undefined) ||
                socialMedia[existingIdx].url,
              profileImgUrl:
                profileImgUrl || socialMedia[existingIdx].profileImgUrl,
              bio:
                (data?.bio as string | undefined) ||
                socialMedia[existingIdx].bio,
              followers: (data?.followers as number) || 0,
              following: (data?.following as number) || 0,
              postsCount: (data?.postsCount as number) || 0,
              timestamp,
            }
          } else {
            socialMedia.push({
              platform,
              userName,
              url: data?.url as string | undefined,
              profileImgUrl,
              bio: data?.bio as string | undefined,
              followers: (data?.followers as number) || 0,
              following: (data?.following as number) || 0,
              postsCount: (data?.postsCount as number) || 0,
              timestamp,
            })
          }

          totalFollowers += (data?.followers as number) || 0
          totalFollowing += (data?.following as number) || 0
          totalPosts += (data?.postsCount as number) || 0
          hasScraperData = true

          if (subject && !subject.imageUrl && isUsableImageUrl(profileImgUrl)) {
            subject = { ...subject, imageUrl: profileImgUrl }
          }
          break
        }

        case 'find:contact_found': {
          const emails = data?.emails as IContactEmail[] | undefined
          const phones = data?.phones as IContactPhone[] | undefined
          const addresses = data?.addresses as IContactAddress[] | undefined
          if (emails) contactInfo.emails = emails
          if (phones) contactInfo.phones = phones
          if (addresses) contactInfo.addresses = addresses
          break
        }

        case 'analysis:post_flagged': {
          const flaggedKey = `flagged:${data?.platform}:${data?.userName}:${
            data?.url ?? data?.content
          }`
          if (seenFindings.has(flaggedKey)) break
          seenFindings.add(flaggedKey)

          const flags = (data?.flags as string[]) || []

          findings.push({
            id: `finding-${i}`,
            kind: EFindingKind.FLAGGED_POST,
            platform: data?.platform as string,
            userName: data?.userName as string,
            type: data?.type as string,
            content: data?.content as string,
            imageUrl: data?.imageUrl as string,
            url: data?.url as string,
            flags,
            date: data?.date as string,
            timestamp,
          })

          redFlagsDetected++
          break
        }

        case 'analysis:pattern_detected': {
          const description = data?.description as string
          if (!description) break
          const subType = data?.type as string

          if (subType === EFindingKind.RELATIONSHIP) {
            const person = (data?.person as string) || ''
            const relKey = `relationship:${person.toLowerCase() || description}`
            if (seenFindings.has(relKey)) break
            seenFindings.add(relKey)

            findings.push({
              id: `finding-${i}`,
              kind: EFindingKind.RELATIONSHIP,
              person,
              handles: (data?.handles as string[] | undefined) ?? [],
              relationshipTone: data?.relationshipTone as
                | ERelationshipTone
                | undefined,
              content: description,
              confidence: data?.confidence as number,
              timestamp,
            })
            break
          }

          const patternKey = `pattern:${description}`
          if (seenFindings.has(patternKey)) break
          seenFindings.add(patternKey)

          findings.push({
            id: `finding-${i}`,
            kind: EFindingKind.PATTERN,
            patternType: subType as EPatternType,
            content: description,
            confidence: data?.confidence as number,
            timestamp,
          })
          break
        }

        case 'find:web_news_found': {
          const newsKey = `news:${data?.url ?? data?.source}`
          if (seenFindings.has(newsKey)) break
          seenFindings.add(newsKey)

          findings.push({
            id: `finding-${i}`,
            kind: EFindingKind.NEWS,
            source: (data?.source as string) || '',
            content: data?.content as string,
            imageUrl: data?.imageUrl as string,
            url: data?.url as string,
            date: data?.date as string,
            timestamp,
          })
          break
        }

        case 'find:digital_footprint_found': {
          const footprintKey = `footprint:${data?.platform}:${data?.url}`
          if (seenFindings.has(footprintKey)) break
          seenFindings.add(footprintKey)

          findings.push({
            id: `finding-${i}`,
            kind: EFindingKind.FOOTPRINT,
            platform: data?.platform as string,
            userName: data?.userName as string,
            content: data?.description as string,
            imageUrl: data?.imageUrl as string,
            url: data?.url as string,
            timestamp,
          })
          break
        }

        case 'find:face_match': {
          const faceKey = `face:${data?.platform}:${data?.url}`
          if (seenFindings.has(faceKey)) break
          seenFindings.add(faceKey)

          findings.push({
            id: `finding-${i}`,
            kind: EFindingKind.FACE_MATCH,
            platform: data?.platform as string,
            userName: data?.userName as string,
            url: data?.url as string,
            imageUrl: data?.imageUrl as string,
            timestamp,
          })
          break
        }
      }
    }

    return {
      subject,
      socialMedia,
      findings: findings.slice(-MAX_FINDINGS),
      contactInfo,
      totals: {
        posts: totalPosts,
        following: totalFollowing,
        followers: totalFollowers,
      },
      stats: {
        redFlagsDetected,
      },
      hasScraperData,
      behavioralSummary,
    }
  }, [events, initialSubject])
}
